import { BankAccountOnFile } from '@liveauctioneers/hammer-ui-payments/checkout';
import { useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';

import BankAccountStatus from '@/enums/bankAccountStatus';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { addPlaidBankAccount } from '@/redux/modules/account/payments/bankAccount/bankAccount.actions';
import { getPlaidLinkToken } from '@/redux/modules/account/payments/bankAccount/bankAccount.selectors';
import { getBidderId, getUserFirstName, getUserLastName } from '@/redux/modules/account/user/user.selectors';
import { BankAccount } from '@/types/bankAccount';
import { formatBankAccountTitle } from '@/utils/bankAccount';

type Props = {
    bankAccount: BankAccount;
    className?: string;
    deleteAccount?: () => void;
};

const onExit = (error, metadata) => {
    // log errors if any
    if (error) {
        console.error(error, metadata);
    }
};

/**
 * Do not use this component directly, use the container.
 * This component has a container because we cannot call the usePlaid link unless we have a token, or it will fail.
 */

const BankAccountOnFileWithPlaidVerify = ({ bankAccount, className, deleteAccount }: Props) => {
    const dispatch = useAppDispatch();

    const lastName = useAppSelector((state) => getUserLastName(state));
    const firstName = useAppSelector((state) => getUserFirstName(state));
    const bidderId = useAppSelector((state) => getBidderId(state));
    const { plaidToken: accessToken } = bankAccount;

    const plaidLinkToken = useAppSelector((state) => getPlaidLinkToken(state, accessToken));

    const onSuccess = useCallback(
        (token, metadata) => {
            dispatch(
                addPlaidBankAccount({
                    accessToken,
                    accountId: metadata.account_id,
                    bidderId,
                    firstName,
                    lastName,
                    token,
                })
            );
        },
        [accessToken, bidderId, dispatch, firstName, lastName]
    );

    const config = {
        onExit,
        onSuccess,
        token: plaidLinkToken,
    };

    const { open } = usePlaidLink(config);

    const pendingAuto = bankAccount.status === BankAccountStatus.PendingAutomatic;
    const pendingManual = bankAccount.status === BankAccountStatus.PendingManual;
    const pending = pendingAuto || pendingManual;

    return (
        <BankAccountOnFile
            className={className}
            hideSetAsDefault
            hideVerify={!pendingManual}
            isDefault={bankAccount.defaultAccount}
            isEditable
            isPending={pending}
            onRemoveClicked={deleteAccount}
            onVerifyClicked={pendingManual ? () => open() : undefined}
            title={formatBankAccountTitle(bankAccount)}
        />
    );
};

export default BankAccountOnFileWithPlaidVerify;
